import { DayTime } from "./DayTime";
import { DatePeriod } from "./date-period";

export class DayTimePeriod {
  from: DayTime;
  to: DayTime;

  constructor(from: DayTime, to: DayTime) {
    this.from = from;
    this.to = to;
  }

  static Parse(from: string, to: string) {
    return new DayTimePeriod(DayTime.Parse(from), DayTime.Parse(to));
  }

  toDatePeriod(date: Date): DatePeriod {
    const from = this.from.toDate(date);
    const to = this.to.toDate(date);

    if (this.from.toSeconds() >= this.to.toSeconds()) {
      to.setDate(to.getDate() + 1);
    }

    return new DatePeriod(from, to);
  }

  durationMinutes() {
    let result = (this.to.toSeconds() - this.from.toSeconds()) / 60;

    if (result < 0) {
      result += 1440; // Add 24 hours if it spans midnight
    }

    return result;
  }

  clamp(time: DayTimePeriod): DayTimePeriod {
    const thisPeriod = normalize(this.from, this.to);
    const otherPeriod = normalize(time.from, time.to);

    // Handle full-day periods
    const thisIsFullDay = thisPeriod.from === 0 && thisPeriod.to === 0;
    const otherIsFullDay = otherPeriod.from === 0 && otherPeriod.to === 0;

    if (thisIsFullDay) {
      return time; // Full-day period clamps to the other period
    }

    if (otherIsFullDay) {
      return this; // Full-day period clamps to this period
    }

    const from = Math.max(thisPeriod.from, otherPeriod.from);
    const to = Math.min(thisPeriod.to, otherPeriod.to);

    // If no overlap, throw an error
    if (from >= to) {
      throw new Error("Periods do not overlap");
    }

    return new DayTimePeriod(
      new DayTime(denormalize(from)),
      new DayTime(denormalize(to))
    );
  }

  overlaps(other: DayTimePeriod): boolean {
    const thisPeriod = normalize(this.from, this.to);
    const otherPeriod = normalize(other.from, other.to);

    // Treat "00:00 to 00:00" as a full-day period (0 to 86400 seconds)
    const thisIsFullDay = thisPeriod.from === 0 && thisPeriod.to === 0;
    const otherIsFullDay = otherPeriod.from === 0 && otherPeriod.to === 0;

    if (thisIsFullDay || otherIsFullDay) {
      return true; // A full-day period overlaps with any other period
    }

    // Check for overlap
    return thisPeriod.from < otherPeriod.to && thisPeriod.to > otherPeriod.from;
  }
}

// Normalize the periods to handle midnight spans
function normalize(from: DayTime, to: DayTime) {
  const fromSeconds = from.toSeconds();
  const toSeconds = to.toSeconds();
  return toSeconds > fromSeconds
    ? { from: fromSeconds, to: toSeconds }
    : { from: fromSeconds, to: toSeconds + 86400 }; // Add 24 hours if it spans midnight
}

function denormalize(seconds: number) {
  return seconds % 86400;
}
